import { useState } from 'react';
import Header from "@/components/Header";
import InfoLayer from "@/components/Profile/InfoLayer";
import ProfileOverview from "@/components/Profile/Overview";
import SectionHeader from "@/components/Profile/SectionHeader";
import WeeklyAvailabilityModal from "@/components/Profile/WeeklyAvailabilityModal";
import AvailabilityDisplay from "@/components/Profile/AvailabilityDisplay";
import { useAuth } from "@/hooks/use-auth";
import { useNavigate } from "react-router-dom";
import { Clock } from 'lucide-react';
import { successToast } from '@/lib/toast';

// Default values for cleaner rendering if data is missing
const defaultAstrologer = {
  credentials: {
    picture: "https://via.placeholder.com/150/CCCCCC/FFFFFF?text=User",
    name: "Acharya Eklavya",
    phoneNumber: "81987689678",
    email: "AcharyaE34@gmail.com",
    verified: true,
    categories: ["Finance", "Career"],
    astrologerTypes: ["KP Astrology"],
    languages: "Hindi, English",
    bio: "Acharya Eklavya is a renowned Vedic astrologer having knowledge of KP system also."
  },
  stats: {
    experience: "3 years"
  },
  paymentDetails: {
    DisplayName: "Acharya Eklavya",
    bankName: "Canara Bank",
    bankAccountNumber: "674497xxxxxx",
    ifscCode: "8785 6676 8979 8575",
    upiId: "2345877644@ybl"
  }
};

export default function ProfilePage() {
  const { astrologer: astrologerData } = useAuth();
  const astrologer = astrologerData || defaultAstrologer;
  const [showWeeklyAvailabilityModal, setShowWeeklyAvailabilityModal] = useState(false);

  const creds = astrologer.personalInformation || defaultAstrologer.credentials;
  const stats = astrologer.stats || defaultAstrologer.stats;
  const payment = astrologer.paymentDetails || defaultAstrologer.paymentDetails;
  const navigate = useNavigate();

  const handleAvailabilitySuccess = (availability) => {
    // console.log('Weekly availability set successfully:', availability);
    successToast("Weekly availability updated successfully!");
    setShowWeeklyAvailabilityModal(false);
  };

  return (
    <>
      <Header name="Profile" />
      <div className="bg-gray-50 max-w-full font-sans">
        {/* General Information Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="flex justify-between items-center mb-6">
            <SectionHeader title="General Information" className="mb-0" />
          </div>

          <div className="flex flex-col md:flex-row gap-8">
            {/* Profile Overview (Left Side) */}
            <div className="w-full md:w-1/4 flex justify-center md:justify-start">
              <ProfileOverview
                image={creds.picture}
                name={creds.name}
                accountType="Personal account"
              />
            </div>

            {/* Info Fields (Right Side) */}
            <div className="w-full md:w-3/4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                <InfoLayer
                  label="Mobile Number"
                  info={creds.phoneNumber}
                />
                <InfoLayer
                  label="Email"
                  info={creds.verified ? `${creds.email} (verified)` : creds.email}
                />
                <InfoLayer
                  label="Years of Experience"
                  info={stats.experience}
                />
                <InfoLayer
                  label="Skills"
                  info={creds.categories}
                />
                <InfoLayer
                  label="Expertise"
                  info={creds.astrologerTypes}
                />
                <InfoLayer
                  label="Languages"
                  info={creds.languages}
                />
              </div>

              <InfoLayer
                label="Bio"
                info={creds.description}
                className="mt-6"
              />
            </div>
          </div>
        </div>

        {/* Weekly Availability Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="flex justify-between items-center mb-6">
            <SectionHeader title="Your Availability" className="mb-0" />
            <button
              onClick={() => setShowWeeklyAvailabilityModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md flex items-center text-sm transition duration-150 ease-in-out"
            >
              <Clock className="h-4 w-4 mr-2" />
              Set Availability
            </button>
          </div>

          <AvailabilityDisplay
            astrologerId={astrologer._id || astrologer.astrologerId}
            onEditClick={() => setShowWeeklyAvailabilityModal(true)}
          />
        </div>

        {/* Payment Details Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <SectionHeader title="Payment Details" className="mb-0" />
            <button
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md flex items-center text-sm transition duration-150 ease-in-out"
              onClick={() => {
                navigate("/profile/edit/payment");
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit Payment Details
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
            <InfoLayer
              label="Display Name"
              info={payment.DisplayName}
            />
            <InfoLayer
              label="Bank Name"
              info={payment.bankName}
            />
            <InfoLayer
              label="Bank Account Number"
              info={payment.bankAccountNumber}
            />
            <InfoLayer
              label="IFSC Code"
              info={payment.ifscCode}
            />
            <InfoLayer
              label="UPI Id"
              info={payment.upi}
            />
          </div>
        </div>
      </div>

      {/* Weekly Availability Modal */}
      <WeeklyAvailabilityModal
        isOpen={showWeeklyAvailabilityModal}
        onClose={() => setShowWeeklyAvailabilityModal(false)}
        astrologerId={astrologer._id || astrologer.astrologerId}
        onSuccess={handleAvailabilitySuccess}
      />
    </>
  );
}